import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { getSafeReturnTo } from './utils/returnTo';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Agents from './pages/Agents';
import KnowledgeHub from './pages/KnowledgeHub';
import Playground from './pages/Playground';
import Tickets from './pages/Tickets';
import Layout from './components/layout/Layout';
import Widget from './pages/Widget';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import BillingPage from './pages/BillingPage';
import Team from './pages/Team';
import Inbox from './pages/Inbox';
import Contacts from './pages/Contacts';
import Reports from './pages/Reports';
import Integrations from './pages/Integrations';
import DataConnectorDocs from './pages/DataConnectorDocs';
import InviteAccept from './pages/InviteAccept';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ShopifyCallback from './pages/ShopifyCallback';
import HelpCenterStudio from './pages/HelpCenterStudio';
const AgentAvatarPage = lazy(() => import('./pages/AgentAvatarPage'));
// AI Stylist marketing/explainer page — Shopify build only (the sidebar nav
// item is build-gated; the route is harmless on the default build, just
// unreachable from the UI). Lazy so its icon/copy don't bloat the initial
// dashboard bundle.
const Stylist = lazy(() => import('./pages/Stylist'));
// Templates — educational page for the Prodagi-curated starter packs. Lazy for
// the same reason as Stylist: explainer copy shouldn't sit in the initial
// dashboard bundle.
const Templates = lazy(() => import('./pages/Templates'));
// Virtual Try-On educational/explainer page — Shopify build only, gated on the
// master launch flag (VITE_VIRTUAL_TRYON_ENABLED). Same lazy/gating pattern as
// the Stylist page; the route is only registered when the flag is on.
const VirtualTryOnPage = lazy(() => import('./pages/VirtualTryOnPage'));
// Orchestrator hub — a single Chat surface (Reports agent + Team Assistant).
// Lazy so the SSE reader and the chat surface stay out of the initial dashboard
// bundle.
const Orchestrator = lazy(() => import('./pages/orchestrator/Orchestrator'));
// Domain Identities — Phase 1a domain-verification surface. Dark-shipped: the
// route is only registered when the runtime feature flag `features.email_domains`
// (from the /api/auth/me hydration payload, mirroring EMAIL_DOMAINS_ENABLED) is
// on. Lazy so it stays out of the initial bundle. Plan-DomainVerification.md §7a.
const DomainIdentities = lazy(() => import('./pages/DomainIdentities'));
// Lazy-loaded so the qrcode.react dep doesn't land in the initial bundle.
const QRCodesPage = lazy(() => import('./pages/QRCodesPage'));
// Public changelog routes — lazy so the DOMPurify body-render path
// and per-page meta-tag helper don't bloat the initial dashboard bundle.
const Changelog = lazy(() => import('./pages/Changelog'));
const ChangelogDetail = lazy(() => import('./pages/ChangelogDetail'));
// Public Shopify install handoff — backend OAuth callback redirects here
// with the JWT in URL fragment (#token=...). Lazy because the page is
// only hit once per install and only on the Shopify build.
const InstallComplete = lazy(() => import('./pages/InstallComplete'));
// Hidden /dev/avatar-test debug page — lazy because it's only loaded by
// devs hitting the URL directly. No sidebar entry. See AvatarTest.tsx
// header for the threat model + scope notes.
const AvatarTest = lazy(() => import('./pages/AvatarTest'));
// Public (no-auth) per-ticket status page — landing for the email magic
// link {FRONTEND_URL}/tickets/track/{public_token}. Lazy because it's only
// hit by emailed customers, never part of the dashboard flow.
const TicketTrack = lazy(() => import('./pages/TicketTrack'));
// Route guard — closes the "type the URL" hole. Draws its permission from the
// SAME nav metadata the sidebar renders (config/navigation.ts), so a route can
// never be more permissive than the menu. Dark until the `rbac_route_guards`
// hydration flag is on. Routes with no nav entry (/reports, /billing, /profile,
// /qr-codes) are deliberately left unguarded — see RequirePerm.tsx.
import RequirePerm from './components/RequirePerm';
import HelpCenterPasswordGate from './pages/HelpCenterPasswordGate';
import VersionUpdateManager from './components/VersionUpdateManager';
import { APP_CONFIG } from './config/appConfig';
import { useEffect } from 'react';

// AI Stylist is a Shopify-build feature. On the default (Stripe) build the
// /stylist route is NOT registered at all, so a direct URL visit falls through
// to no-match (never reaches the marketing page). Per-agent activation is
// governed separately by the config.stylist_enabled toggle.
const IS_SHOPIFY_BUILD = APP_CONFIG.billingProvider === 'shopify';
const STYLIST_ENABLED = IS_SHOPIFY_BUILD;
// Virtual Try-On is a Shopify-build feature behind its own master launch flag
// (VITE_VIRTUAL_TRYON_ENABLED=true). When off, the /virtual-try-on route is NOT
// registered, so a direct URL visit falls through to no-match until launched.
const VIRTUAL_TRYON_ENABLED = IS_SHOPIFY_BUILD && APP_CONFIG.virtualTryonEnabled;

function App() {
  const { isAuthenticated, loading, user } = useAuth();
  // Runtime dark-ship gate for the Domain Identities hub. The page hosts two
  // independently-flagged sections (email sending domains + Help Center domain),
  // each mirroring its own backend env gate via the hydration payload. The route
  // exists when EITHER flag is on; each section self-gates inside the page.
  const domainIdentitiesEnabled =
    !!user?.features?.email_domains ||
    !!user?.features?.help_center_custom_domain;

  useEffect(() => {
    // Preheat backend
    const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    fetch(`${backendUrl}/health`)
      .catch(() => {
        // Ignore errors, just waking up the service
        console.log('Backend preheat attempted');
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  /**
   * Auth-route guard: when an already-authed user lands on /login (or /, /register,
   * /forgot-password), honor a `?return_to=…` query param via the same allowlist
   * the form-submit success path uses (`utils/returnTo.ts`).
   *
   * Triggered by Storm's Phase 2 customer-JWT gate on the admin HC: a logged-in
   * user clicking the Prodagi HC icon hits 302 → /login?return_to=<HC URL>; if
   * we render <Navigate to="/dashboard"> here, the query param is dropped and
   * the user lands on the dashboard instead of the HC. Pre-Phase-2 this code
   * was unreachable for authed users (no return_to ever appeared on /login).
   *
   * Behavior:
   *   - Relative `return_to` (same-origin path) → SPA `<Navigate>` (synchronous).
   *   - Absolute `return_to` (`https://*.prodagi.ai` or
   *     `https://*.askprodigy.ai` — both live, both allowlisted — or
   *     localhost dev) → full
   *     page reload via `window.location.href` so React Router doesn't try to
   *     SPA-route to an external URL.
   *   - No / invalid `return_to` → falls through to `defaultPath` (default `/dashboard`).
   */
  const RedirectIfAuthed = ({ defaultPath = '/dashboard' }: { defaultPath?: string }) => {
    const target = getSafeReturnTo(defaultPath);
    const isAbsolute = /^https?:\/\//i.test(target);
    useEffect(() => {
      if (isAbsolute) {
        window.location.href = target;
      }
    }, [target, isAbsolute]);
    if (isAbsolute) return null;
    return <Navigate to={target} replace />;
  };

  return (
    <BrowserRouter>
      {/* Stale-frontend version controller — detects an outdated bundle and
          auto-refreshes at a safe moment. Self-disables on the /widget route
          (PROTECTED #1). Sibling to <Routes> so it always sees route changes
          via useLocation. */}
      <VersionUpdateManager />
      <Routes>
        <Route path="/widget/:agentId" element={<Widget />} />

        {/* Public Routes */}
        <Route path="/" element={isAuthenticated ? <RedirectIfAuthed /> : <Login />} />
        <Route path="/login" element={isAuthenticated ? <RedirectIfAuthed /> : <Login />} />
        <Route path="/register" element={isAuthenticated ? <RedirectIfAuthed /> : <Register />} />
        <Route path="/forgot-password" element={isAuthenticated ? <RedirectIfAuthed /> : <ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        {/* Public read-only customer ticket status page (no auth) — the
            landing for the per-ticket email magic link. Sits with the other
            public token routes; renders its own branded chrome (not Layout). */}
        <Route
          path="/tickets/track/:token"
          element={
            <Suspense fallback={null}>
              <TicketTrack />
            </Suspense>
          }
        />
        {/* Shopify OAuth landing — always rendered (it establishes the session);
            do NOT wrap in RedirectIfAuthed, it must run its exchange effect. */}
        <Route path="/auth/shopify/callback" element={<ShopifyCallback />} />
        {/* Shopify install handoff — backend install callback redirects
            here with the JWT in URL fragment so the token never appears
            in server / CDN logs or Referer headers. Public route (no
            auth guard); the page itself establishes the session. */}
        <Route
          path="/auth/install-complete"
          element={
            <Suspense fallback={null}>
              <InstallComplete />
            </Suspense>
          }
        />

        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/help/:workspaceSlug/auth" element={<HelpCenterPasswordGate />} />
        {/* Public QR-codes page — shareable URL with QR codes for the three
            Prodagi properties. No auth, no API calls. Lazy-loaded so the
            qrcode.react dep doesn't bloat the initial bundle. */}
        <Route
          path="/qr-codes"
          element={
            <Suspense fallback={null}>
              <QRCodesPage />
            </Suspense>
          }
        />
        {/* Public changelog — anon-safe listing + per-entry detail. Routes
            sit outside RequireAuth so unauth visitors and Shopify-build
            sessions both reach them. Plan-ChangelogBanner.md §Phase 2a. */}
        <Route
          path="/changelog"
          element={
            <Suspense fallback={null}>
              <Changelog />
            </Suspense>
          }
        />
        <Route
          path="/changelog/:slug"
          element={
            <Suspense fallback={null}>
              <ChangelogDetail />
            </Suspense>
          }
        />

        {/* Protected Dashboard Routes */}
        <Route
          path="/dashboard"
          element={isAuthenticated ? <Layout><RequirePerm><Dashboard /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/agents"
          element={isAuthenticated ? <Layout><RequirePerm><Agents /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        {STYLIST_ENABLED && (
          <Route
            path="/stylist"
            element={isAuthenticated ? <Layout><RequirePerm><Suspense fallback={null}><Stylist /></Suspense></RequirePerm></Layout> : <Navigate to="/login" />}
          />
        )}
        {VIRTUAL_TRYON_ENABLED && (
          <Route
            path="/virtual-try-on"
            element={isAuthenticated ? <Layout><RequirePerm><Suspense fallback={null}><VirtualTryOnPage /></Suspense></RequirePerm></Layout> : <Navigate to="/login" />}
          />
        )}
        {domainIdentitiesEnabled && (
          <Route
            path="/domain-identities"
            element={isAuthenticated ? <Layout><RequirePerm><Suspense fallback={null}><DomainIdentities /></Suspense></RequirePerm></Layout> : <Navigate to="/login" />}
          />
        )}
        <Route
          path="/templates"
          element={isAuthenticated ? <Layout><RequirePerm><Suspense fallback={null}><Templates /></Suspense></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/knowledge"
          element={isAuthenticated ? <Layout><RequirePerm><KnowledgeHub /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/help-center"
          element={isAuthenticated ? <Layout><RequirePerm><HelpCenterStudio /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/playground"
          element={isAuthenticated ? <Layout><RequirePerm><Playground /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/agent-avatar"
          element={isAuthenticated ? <Layout><RequirePerm><Suspense fallback={null}><AgentAvatarPage /></Suspense></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/tickets"
          element={isAuthenticated ? <Layout><RequirePerm><Tickets /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/profile"
          element={isAuthenticated ? <Layout><Profile /></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/settings"
          element={isAuthenticated ? <Layout><RequirePerm><Settings /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/billing"
          element={isAuthenticated ? <Layout><BillingPage /></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/team"
          element={isAuthenticated ? <Layout><RequirePerm><Team /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/inbox"
          element={isAuthenticated ? <Layout><RequirePerm><Inbox /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/contacts"
          element={isAuthenticated ? <Layout><RequirePerm><Contacts /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/reports"
          element={isAuthenticated ? <Layout><Reports /></Layout> : <Navigate to="/login" />}
        />
        {/* Orchestrator hub — chat only, so the page is the whole surface. */}
        <Route
          path="/orchestrator"
          element={isAuthenticated ? <Layout><RequirePerm><Suspense fallback={null}><Orchestrator /></Suspense></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        {/* Legacy sub-surface URLs (/orchestrator/chat, /insights, ...) from
            when the hub had tabs. Kept as a redirect so old links and browser
            history land on the hub instead of 404ing. */}
        <Route path="/orchestrator/:tab" element={<Navigate to="/orchestrator" replace />} />
        <Route
          path="/integrations"
          element={isAuthenticated ? <Layout><RequirePerm><Integrations /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        <Route
          path="/integrations/docs"
          element={isAuthenticated ? <Layout><RequirePerm navHref="/integrations"><DataConnectorDocs /></RequirePerm></Layout> : <Navigate to="/login" />}
        />
        {/* Hidden dev page — staff-only smoke-test for the avatar-service.
            NOT linked from the sidebar; reachable via direct URL only.
            Preserve the path on redirect so post-login lands back here via
            returnTo.ts's `return_to` honor mechanism. */}
        <Route
          path="/dev/avatar-test"
          element={
            isAuthenticated ? (
              <Suspense fallback={null}>
                <AvatarTest />
              </Suspense>
            ) : (
              <Navigate to="/login?return_to=%2Fdev%2Favatar-test" replace />
            )
          }
        />
        {/* No-match catch-all. Must stay LAST. Added when the legacy marketing
            routes (/waitlist, /about, /terms, /privacy, /faq, /pricing) were
            removed — those pages now live on the separate WordPress site, and
            without this a stale link would render a blank screen. Mirrors the
            admin app's convention (admin-front/src/App.tsx). */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
